/**
 * Переиспользуемый выбор встреч для «анализа по списку» (TM-4c1fb82b, п.2–3).
 *
 * Один хук `useMeetingSelection` + фабрика колонки `makeMeetingSelectColumn`
 * покрывают все три таблицы с выбором (реестр встреч, карточка клиента,
 * карточка сотрудника):
 *
 * - п.2: размер чекбоксов задаётся ОДНОЙ константой `SELECT_CHECKBOX_CLASS`
 *   (вдвое крупнее прежнего дефолта), чтобы менять в одном месте.
 * - п.3: чекбокс «выделить все» в шапке колонки. Клик добавляет к выбору ВСЕ
 *   строки, видимые под текущим фильтром (накопительно — смена/сброс фильтра
 *   не сбрасывает ранее выбранное); повторный клик при полностью выделенной
 *   видимой странице снимает выбор ТОЛЬКО с видимых строк. Состояние шапки:
 *   checked = все видимые выбраны, indeterminate = часть, unchecked = ни одной.
 *   Счётчик «Анализ выбранных (N)» — общий размер выбора (включая невидимые).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef, Table } from "@tanstack/react-table";
import type { MeetingSummary, UUID } from "@/api/types";

/**
 * Класс размера чекбокса выбора встреч — единая точка правки (п.2).
 * Среднее между браузерным дефолтом (~16px) и первой итерацией (32px):
 * 24px = `h-6 w-6` — финальный размер по фидбеку Dmitri (TaskMe 18:20).
 */
export const SELECT_CHECKBOX_CLASS = "h-6 w-6 cursor-pointer accent-primary";

export interface MeetingSelection {
  /** Все выбранные id (включая невидимые под текущим фильтром). */
  selectedIds: Set<UUID>;
  /** Общее число выбранных — счётчик кнопки «Анализ выбранных (N)». */
  count: number;
  isSelected: (id: UUID) => boolean;
  /** Добавить/убрать одну строку. */
  toggleOne: (id: UUID, checked: boolean) => void;
  /**
   * Накопительный toggle по видимым строкам: если ВСЕ видимые уже выбраны —
   * снять их; иначе — добавить все видимые к выбору.
   */
  toggleVisible: (visibleIds: UUID[]) => void;
  /** Полный сброс выбора (после успешной отправки анализа). */
  clear: () => void;
}

/**
 * Состояние выбора встреч для таблицы с чекбокс-колонкой.
 *
 * Возвращает СТАБИЛЬНЫЙ по ссылке объект (live-поля обновляются на каждом
 * рендере через ref): благодаря этому колонку выбора можно мемоизировать один
 * раз, и переключение выбора обновляет чекбоксы на месте, а не ремаунтит всю
 * колонку (и все ячейки) на каждый клик.
 */
export function useMeetingSelection(): MeetingSelection {
  const [selectedIds, setSelectedIds] = useState<Set<UUID>>(() => new Set());

  const toggleOne = useCallback((id: UUID, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleVisible = useCallback((visibleIds: UUID[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const ref = useRef<MeetingSelection | null>(null);
  if (ref.current === null) {
    ref.current = {
      selectedIds,
      count: selectedIds.size,
      isSelected: (id) => selectedIds.has(id),
      toggleOne,
      toggleVisible,
      clear,
    };
  } else {
    // Live-поля читают актуальный selectedIds; методы стабильны (functional
    // setState), поэтому идентичность объекта не меняется между рендерами.
    ref.current.selectedIds = selectedIds;
    ref.current.count = selectedIds.size;
    ref.current.isSelected = (id) => selectedIds.has(id);
  }
  return ref.current;
}

function stop(e: React.SyntheticEvent): void {
  e.stopPropagation();
}

/** Чекбокс «выделить все» в шапке — состояние по видимым строкам таблицы. */
function SelectAllHeader({
  selection,
  table,
}: {
  selection: MeetingSelection;
  table: Table<MeetingSummary>;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const visibleIds = table.getRowModel().rows.map((r) => r.original.id);
  const selectedVisible = visibleIds.filter((id) =>
    selection.isSelected(id),
  ).length;
  const checked =
    visibleIds.length > 0 && selectedVisible === visibleIds.length;
  const indeterminate = selectedVisible > 0 && !checked;

  // indeterminate — только DOM-свойство, задаётся императивно.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={SELECT_CHECKBOX_CLASS}
      checked={checked}
      onClick={stop}
      onChange={(e) => {
        stop(e);
        selection.toggleVisible(visibleIds);
      }}
      aria-label={t("listAnalysis.select.allAria")}
      data-testid="select-all-meetings"
    />
  );
}

/** Чекбокс одной строки. */
function SelectRowCheckbox({
  selection,
  id,
}: {
  selection: MeetingSelection;
  id: UUID;
}) {
  return (
    <input
      type="checkbox"
      className={SELECT_CHECKBOX_CLASS}
      checked={selection.isSelected(id)}
      onClick={stop}
      onChange={(e) => {
        stop(e);
        selection.toggleOne(id, e.target.checked);
      }}
      aria-label={`select meeting ${id}`}
      data-testid={`select-meeting-${id}`}
    />
  );
}

/**
 * Колонка выбора для `DataTable<MeetingSummary>` — чекбокс-шапка «выделить
 * все» + чекбокс на строку. Одна фабрика на все три таблицы.
 */
export function makeMeetingSelectColumn(
  selection: MeetingSelection,
): ColumnDef<MeetingSummary, unknown> {
  return {
    id: "select",
    header: ({ table }) => (
      <SelectAllHeader selection={selection} table={table} />
    ),
    cell: ({ row }) => (
      <SelectRowCheckbox selection={selection} id={row.original.id} />
    ),
  };
}
